import { useEffect, useState } from "react";
import { Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../lib/api";

const ROLES = [
  ["", "Not decided yet"],
  ["operator", "Operator of the whole platform"],
  ["delivery", "Kenyan delivery and project vehicle"],
  ["holding", "Holds Kenyan contracts and land locally"],
];

const FIELDS = [
  ["company_name", "Registered name, exactly as on the certificate"],
  ["company_number", "Kenyan company registration number"],
  ["registered_office", "Registered office address"],
  ["relationship", "Relationship to KickStartKenyaFootball"],
];

const blank = { authority: "", held_in_name_of: "", number: "", granted_on: "", covers: "" };

/** The Kenyan company and the registrations actually held. Nothing publishes until it is entered here. */
export default function KenyaEntity() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let live = true;
    api.get("/admin/settings/kenya-entity")
      .then((r) => setForm((prev) => (live && prev === null ? { registrations: [], role: "", ...r.data } : prev)))
      .catch((err) => toast.error(errText(err)));
    return () => { live = false; };
  }, []);

  if (!form) return <p className="text-zinc-600" data-testid="kenya-entity-loading">Loading…</p>;

  const set = (key) => (e) => setForm({ ...form, [key]: e?.target ? e.target.value : e });
  const setRow = (i, key) => (e) =>
    setForm({ ...form, registrations: form.registrations.map((r, j) => (j === i ? { ...r, [key]: e.target.value } : r)) });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings/kenya-entity", form);
      setForm({ registrations: [], role: "", ...data });
      toast.success("Recorded — the site now publishes only what is entered here");
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} data-testid="admin-kenya-entity">
      <p className="overline">Structure</p>
      <h1 className="display-2 mt-4">Kenyan entity and registrations</h1>
      <p className="mt-4 max-w-3xl text-sm leading-relaxed text-zinc-400">
        The site publishes a Kenyan company only once its registered name and number are both entered here, and a
        registration only once its authority, number and date are all entered. Leave a field empty and the site
        says nothing about it — it will never claim a registration nobody has recorded.
      </p>

      <div className="mt-10 grid gap-5 border border-zinc-900 bg-ink-surface p-6 md:grid-cols-2 md:p-8">
        {FIELDS.map(([key, label]) => (
          <div key={key}>
            <label className="overline">{label}</label>
            <input className="field mt-2" value={form[key] || ""} onChange={set(key)} data-testid={`kenya-${key}`} />
          </div>
        ))}
        <div>
          <label className="overline">Its role</label>
          <select className="field mt-2" value={form.role || ""} onChange={set("role")} data-testid="kenya-role">
            {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-8 border border-zinc-900 bg-ink-surface p-6 md:p-8">
        <p className="overline">Registrations held</p>
        <p className="mt-3 max-w-3xl text-xs leading-relaxed text-zinc-500">
          Only registrations already granted. Authority, number and date are all required before a line is published.
        </p>
        <div className="mt-5 space-y-4" data-testid="kenya-registrations">
          {(form.registrations || []).map((r, i) => (
            <div key={i} className="grid gap-3 border border-zinc-900 p-4 md:grid-cols-[1.2fr_1fr_0.9fr_0.8fr_1.2fr_auto]">
              <input className="field" placeholder="Authority (e.g. ODPC, EARB)" value={r.authority || ""} onChange={setRow(i, "authority")} data-testid={`kenya-reg-authority-${i}`} />
              <input className="field" placeholder="Held in the name of" value={r.held_in_name_of || ""} onChange={setRow(i, "held_in_name_of")} data-testid={`kenya-reg-name-${i}`} />
              <input className="field" placeholder="Number" value={r.number || ""} onChange={setRow(i, "number")} data-testid={`kenya-reg-number-${i}`} />
              <input className="field" placeholder="Granted (e.g. May 2026)" value={r.granted_on || ""} onChange={setRow(i, "granted_on")} data-testid={`kenya-reg-date-${i}`} />
              <input className="field" placeholder="What it covers" value={r.covers || ""} onChange={setRow(i, "covers")} data-testid={`kenya-reg-covers-${i}`} />
              <button type="button" aria-label="Remove registration" data-testid={`kenya-reg-remove-${i}`}
                onClick={() => setForm({ ...form, registrations: form.registrations.filter((_, j) => j !== i) })}
                className="border border-zinc-800 px-3 text-zinc-500 hover:text-kenya-red">
                <X size={14} />
              </button>
            </div>
          ))}
          <button type="button" data-testid="kenya-add-registration" className="btn-ghost !px-5 !py-2.5"
            onClick={() => setForm({ ...form, registrations: [...(form.registrations || []), { ...blank }] })}>
            <Plus size={13} /> Add a registration
          </button>
        </div>
      </div>

      <div className="mt-8 border border-zinc-900 bg-ink-surface p-6 md:p-8">
        <p className="overline">Professional review</p>
        <p className="mt-3 max-w-3xl text-xs leading-relaxed text-zinc-500">
          Tick only what has genuinely happened and could be evidenced. Once ticked, the site states it as a fact.
        </p>
        <div className="mt-5 space-y-3">
          {[["reviewed_by_kenyan_advocate", "Reviewed by an advocate of the High Court of Kenya"],
            ["reviewed_by_uk_solicitor", "Reviewed by a solicitor qualified outside Kenya"]].map(([key, label]) => (
            <label key={key} className="flex items-center gap-3 text-sm text-zinc-300">
              <input type="checkbox" checked={!!form[key]} data-testid={`kenya-${key}`}
                onChange={(e) => setForm({ ...form, [key]: e.target.checked })} />
              {label}
            </label>
          ))}
          <div className="max-w-xs">
            <label className="overline">Month of review</label>
            <input className="field mt-2" placeholder="e.g. June 2026" value={form.reviewed_on || ""} onChange={set("reviewed_on")} data-testid="kenya-reviewed_on" />
          </div>
        </div>
      </div>

      <button type="submit" disabled={saving} className="btn-primary mt-8 disabled:opacity-60" data-testid="kenya-save">
        {saving ? <Loader2 className="animate-spin" size={14} /> : null} Save structure
      </button>
    </form>
  );
}
